import * as React from 'react'
import {ReactElement} from 'react'
import {
    NatBeforeContactAddress,
    NatContactInformation,
    NatDestinationContactAddress,
    NatGuestOwnershipTransferNewMemberLocationState,
    NatPaymentContactAddress,
    NatProxyContactAddress,
    NatSharer,
} from './newMember/NatGuestOwnershipTransferNewMemberInterface'
import {Nullable} from '../../../../app/TypeHelpers'
import {BlockSpacer} from '../../../../styledComponents/ResponsiveConvenience'
import {DisplayItemDividerForPageTop} from '../../../../styledComponents/DisplayItem'
import {PageTitle} from '../../applications/common/resident/NatResidentApplicationsCommon'
import {Box} from '@mui/material'
import {
    HeadingAreaMessageComponentForResident,
} from '../../../../components/shared/pageBaseComponents/HeadingAreaMessageComponentForResident'
import NatGuestNewMemberPageForm from './newMember/NatGuestNewMemberPageForm'
import {NatOwnershipTransfersRepo} from '../repository/NatOwnershipTransfersRepo'
import NatCondominiumRepo from '../../condominium/repository/NatCondominiumRepo'
import NatCurrentDateTimeRepo from '../../currentDateTime/repository/NatCurrentDateTimeRepo'
import HolidaysRepository from '../../../../components/HolidaysRepository'
import {NatPutNewMemberRequest, NatPutOwnershipTransferNewMemberResponse} from '../repository/NatPutNewMemberRequest'
import {Messages} from '../../../../Messages'
import {GuestBackButton, GuestPlainLinkStyledBackButton} from '../common/NatOwnershipTransfersCommon'
import {PREFECTURES_MASTER, RESIDENT_PLACE} from '../../../../CodeMaster'
import {
    createGuestWithErrorHandlingPage,
    GuestWithErrorHandlingPagePropsSet,
} from '../../../../components/shared/hoc/createGuestWithErrorHandlingPage'
import {convertToRoomNumberForResident} from '../../../common/util/Natutil'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    applyId?: string
    ownershipTransfersRepo: NatOwnershipTransfersRepo
    condominiumRepo: NatCondominiumRepo
    currentDateTimeRepo: NatCurrentDateTimeRepo
    holidaysRepo: HolidaysRepository
    location: Location<NatGuestOwnershipTransferNewMemberLocationState>
}

interface State {
    familyName: Nullable<string>
    firstName: Nullable<string>
    familyNameKana: Nullable<string>
    firstNameKana: Nullable<string>
    mailAddress: Nullable<string>
    mailAddressConfirm: Nullable<string>
    sharerList: NatSharer[]
    residentPlace: Nullable<string>
    moveInDate: Nullable<string>
    contactInformation: Nullable<NatContactInformation>
    beforeContactAddress: Nullable<NatBeforeContactAddress>
    proxyContactAddress: Nullable<NatProxyContactAddress>
    rentExistence: Nullable<string>
    paymentExistence: Nullable<string>
    paymentContactAddress: Nullable<NatPaymentContactAddress>
    destinationExistence: Nullable<string>
    destinationContactAddress: Nullable<NatDestinationContactAddress>
    version: Nullable<number>
}

class NatGuestNewMemberInputPage
    extends React.Component<Props & GuestWithErrorHandlingPagePropsSet, State> {
    constructor(props: Props & GuestWithErrorHandlingPagePropsSet) {
        super(props)

        this.state = {
            familyName: null,
            firstName: null,
            familyNameKana: null,
            firstNameKana: null,
            mailAddress: null,
            mailAddressConfirm: null,
            sharerList: [],
            residentPlace: null,
            moveInDate: null,
            contactInformation: null,
            beforeContactAddress: null,
            proxyContactAddress: null,
            rentExistence: null,
            paymentExistence: null,
            paymentContactAddress: null,
            destinationExistence: null,
            destinationContactAddress: null,
            version: null,
        }

        this.onClickApplyConfirmButton = this.onClickApplyConfirmButton.bind(this)
        this.handleBackLinkOnClick = this.handleBackLinkOnClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {currentDateTimeRepo, eventEmitter} = this.props
        const {inputHistory, condominiumInfo} = this.props.location.state
        await this.props.asyncCallFunction(
            currentDateTimeRepo.getCurrentDateTime.bind(currentDateTimeRepo),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            },
        )

        if (inputHistory?.outlineInfo) {
            let defaultContactAddress: Nullable<NatContactInformation> = null

            if (inputHistory?.outlineInfo.residentPlace === RESIDENT_PLACE.RESIDING.value && condominiumInfo) {
                const buildingRoomNumber = condominiumInfo.condominiumName + '　'
                    + convertToRoomNumberForResident(condominiumInfo.roomNumber)
                defaultContactAddress = {
                    contactPhoneNumber: null,
                    contactAddress: null,
                    contactZipCode: condominiumInfo.zipCode,
                    contactPrefecture: condominiumInfo.prefectureCd ?
                        this.getPrefectureName(condominiumInfo.prefectureCd) : null,
                    contactAddress2: condominiumInfo.address2,
                    contactAddress3: condominiumInfo.address3,
                    contactBuildingRoomNumber: buildingRoomNumber,
                }
            }

            if (inputHistory?.detailInfo) {
                this.setState({
                    familyName: inputHistory.outlineInfo.familyName ?? null,
                    firstName: inputHistory.outlineInfo.firstName ?? null,
                    familyNameKana: inputHistory.outlineInfo.familyNameKana ?? null,
                    firstNameKana: inputHistory.outlineInfo.firstNameKana ?? null,
                    mailAddress: inputHistory.outlineInfo.mailAddress ?? null,
                    mailAddressConfirm: inputHistory.outlineInfo.mailAddressConfirm ?? null,
                    sharerList: inputHistory.outlineInfo.sharer ?? [],
                    residentPlace: inputHistory.outlineInfo.residentPlace ?? null,
                    moveInDate: inputHistory.detailInfo.moveInDate ?? null,
                    contactInformation: inputHistory.detailInfo.contactInformation ?? defaultContactAddress ?? null,
                    beforeContactAddress: inputHistory.detailInfo.beforeContactAddress ?? null,
                    proxyContactAddress: inputHistory.detailInfo.proxyContactAddress ?? null,
                    rentExistence: inputHistory.detailInfo.rentExistence ?? null,
                    paymentExistence: inputHistory.detailInfo.paymentExistence ?? '0',
                    paymentContactAddress: inputHistory.detailInfo.paymentContactAddress ?? null,
                    destinationExistence: inputHistory.detailInfo.destinationExistence ?? '0',
                    destinationContactAddress: inputHistory.detailInfo.destinationContactAddress ?? null,
                    version: inputHistory.detailInfo.version ?? null,
                })
            } else {
                this.setState({
                    familyName: inputHistory?.outlineInfo.familyName ?? null,
                    firstName: inputHistory?.outlineInfo.firstName ?? null,
                    familyNameKana: inputHistory?.outlineInfo.familyNameKana ?? null,
                    firstNameKana: inputHistory?.outlineInfo.firstNameKana ?? null,
                    mailAddress: inputHistory?.outlineInfo.mailAddress ?? null,
                    mailAddressConfirm: inputHistory?.outlineInfo.mailAddressConfirm ?? null,
                    sharerList: inputHistory?.outlineInfo.sharer ?? [],
                    residentPlace: inputHistory?.outlineInfo.residentPlace ?? null,
                    contactInformation: defaultContactAddress ?? null,
                })
            }
        }
    }

    getPrefectureName(prefectureCd: string): string {
        const prefecturesMaster = PREFECTURES_MASTER.codeToEnumConstants(prefectureCd)
        if (prefecturesMaster) {
            return prefecturesMaster.label
        } else {
            return ''
        }
    }

    async onClickApplyConfirmButton(request: NatPutNewMemberRequest): Promise<void> {
        const {ownershipTransfersRepo, condominiumCd} = this.props

        await this.props.eventEmitter.showLoading()
        try {
            const response: Nullable<NatPutOwnershipTransferNewMemberResponse> =
                await this.props.asyncCallFunction(
                    ownershipTransfersRepo.applyOwnershipTransferNewMember.bind(
                        ownershipTransfersRepo, condominiumCd, request),
                    async (httpErrorOrNetworkError) => {
                        await httpErrorOrNetworkError.broadcast(this.props.eventEmitter)
                    },
                    {
                        defaultErrorTitle: Messages.EOPCR0014.toString(),
                        defaultErrorTitlePriority: true,
                        replacementDetailErrorMessages: [{
                            targetMessageId: Messages.EOPCA0004,
                            replacementMessageId: Messages.EOPCR0015,
                        }],
                    },
                )

            if (response && response.requestError === null) {
                const locationState: NatGuestOwnershipTransferNewMemberLocationState = {
                    inputHistory: {
                        outlineInfo: {
                            familyName: request.familyName,
                            firstName: request.firstName,
                            familyNameKana: request.familyNameKana,
                            firstNameKana: request.firstNameKana,
                            mailAddress: request.mailAddress,
                            mailAddressConfirm: request.mailAddressConfirm,
                            sharer: request.sharer,
                            residentPlace: request.residentPlace,
                        },
                        detailInfo: {
                            moveInDate: request.moveInDate,
                            contactInformation: request.contactInformation,
                            beforeContactAddress: request.beforeContactAddress,
                            proxyContactAddress: request.proxyContactAddress,
                            rentExistence: request.rentExistence,
                            paymentExistence: request.paymentExistence,
                            paymentContactAddress: request.paymentContactAddress,
                            destinationExistence: request.destinationExistence,
                            destinationContactAddress: request.destinationContactAddress,
                            version: this.state.version,
                        },
                    },
                    applyId: this.props.location.state?.applyId,
                    ownershipTransferDate: this.props.location.state.ownershipTransferDate,
                    condominiumInfo: this.props.location.state.condominiumInfo,
                    isApply: true,
                }
                this.props.navigate(
                    `/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/confirm/${this.props.applyId ?? ''}`,
                    {
                        replace: true,
                        state: JSON.parse(JSON.stringify(locationState)),
                    },
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleBackLinkOnClick(): void {
        const locationState: NatGuestOwnershipTransferNewMemberLocationState = this.props.location.state
        this.props.navigate(`/guest/condominiums/${this.props.condominiumCd}/applications/ownership-transfers/new-member/outline/${this.props.applyId ?? ''}`,
            {
                replace: true,
                state: JSON.parse(JSON.stringify(locationState)),
            },
        )
    }

    render(): ReactElement {
        return (
            <>
                <GuestPlainLinkStyledBackButton onClick={this.handleBackLinkOnClick}>戻る</GuestPlainLinkStyledBackButton>
                <BlockSpacer height={'15px'}/>
                <DisplayItemDividerForPageTop/>
                <BlockSpacer height={'30px'}/>
                <PageTitle>
                    組合員資格変更届（２/２）
                </PageTitle>
                <Box height={'5px'}/>
                <HeadingAreaMessageComponentForResident eventEmitter={this.props.eventEmitter}/>
                <NatGuestNewMemberPageForm
                    {...this.props}
                    {...this.state}
                    submitButtonLabel={'申請内容を確認する'}
                    onClickApplyConfirmButton={this.onClickApplyConfirmButton}
                />
                <Box height={'30px'}/>
                <GuestBackButton onClick={this.handleBackLinkOnClick}>戻る</GuestBackButton>
                <Box height={'15px'}/>
            </>
        )
    }
}

export default createGuestWithErrorHandlingPage(NatGuestNewMemberInputPage)
